import { forwardRef } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Icon, type IconName } from './Icon';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-action text-action-contrast border border-action hover:bg-action-hover active:bg-action-active',
  secondary:
    'bg-surface text-content border border-line hover:bg-surface-hover hover:border-line-strong',
  ghost: 'bg-transparent text-content-link border border-transparent hover:bg-action-subtle',
  danger:
    'bg-danger text-danger-contrast border border-danger hover:bg-danger-hover active:bg-danger-hover',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-dense-lg gap-1.5 rounded-md',
  md: 'h-9 px-4 text-body gap-2 rounded-md',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconStart?: IconName;
  iconEnd?: IconName;
  loading?: boolean;
  /** Shown as a tooltip when the control is present but not permitted. */
  disabledReason?: string | null;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    iconStart,
    iconEnd,
    loading = false,
    disabled,
    disabledReason,
    fullWidth,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      title={isDisabled && disabledReason ? disabledReason : rest.title}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap font-medium',
        'transition-colors duration-fast ease-standard',
        'disabled:cursor-not-allowed disabled:border-line disabled:bg-surface-sunken disabled:text-content-disabled',
        focusRing,
        sizeClasses[size],
        variantClasses[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner size={size === 'sm' ? 14 : 16} />
      ) : iconStart ? (
        <Icon name={iconStart} size={size === 'sm' ? 14 : 16} />
      ) : null}
      {children}
      {iconEnd && !loading ? <Icon name={iconEnd} size={size === 'sm' ? 14 : 16} /> : null}
    </button>
  );
});
